import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesOverview = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const sectionRef = useRef(null);

  useEffect(() => {
    fetchServices();
    fetchSettings();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            services.forEach((_, index) => {
              setTimeout(() => {
                setVisibleCards((prev) => [...prev, index]);
              }, index * 200);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current && services.length > 0) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [services]);

  const primaryColor = settings.primary_color || '#0A1A50';
  const secondaryColor = settings.secondary_color || '#B5BBC4';

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Title */}
        <div className="text-center mb-12 px-4">
          <h2 
            className="text-3xl sm:text-4xl md:text-5xl font-light mb-4"
            style={{ color: primaryColor }}
          >
            {settings.services_title || 'Our Services'}
          </h2>
          <p 
            className="text-lg sm:text-xl font-light"
            style={{ color: secondaryColor }}
          >
            {settings.services_subtitle || 'Comprehensive real estate solutions tailored to your ambitions'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <Link
              key={service.id}
              to={`/services/${service.service_id}`}
              className="group cursor-pointer"
              style={{
                opacity: visibleCards.includes(index) ? 1 : 0,
                transform: visibleCards.includes(index) ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
              }}
            >
              <div className="relative h-64 overflow-hidden mb-4 rounded-lg shadow-lg group-hover:shadow-2xl transition-all duration-500">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 group-hover:rotate-2 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div 
                  className="absolute inset-0 border-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"
                  style={{ borderColor: primaryColor }}
                ></div>
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-110">
                  <ArrowRight 
                    className="w-6 h-6"
                    style={{ color: primaryColor }}
                  />
                </div>
              </div>
              <h3 
                className="text-3xl font-light mb-2 group-hover:translate-x-2 transition-all duration-300"
                style={{ color: primaryColor }}
              >
                {service.title}
              </h3>
              <p 
                className="font-light leading-relaxed group-hover:text-gray-800 transition-colors duration-300"
                style={{ color: secondaryColor }}
              >
                {service.description}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;